'use client';

import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  beginAuthRecovery,
  reportTerminalRefreshOutcome,
} from '@/lib/authExpiryGuard';
import {
  classifyAuthRefreshError,
  noteExternalRefreshAttempt,
} from '@/lib/authTokenRefresh';
import { AuthRefreshOutcome } from '@/types/auth-types';

export interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
}

type AuthListener = (state: AuthState) => void;

let authState: AuthState = {
  session: null,
  user: null,
  loading: true,
};

let initialized = false;
let readyPromise: Promise<void> | null = null;
let readyResolve: (() => void) | null = null;
let hasAuthEvent = false;
let authSubscription: ReturnType<typeof supabase.auth.onAuthStateChange>['data'] | null = null;
const listeners = new Set<AuthListener>();

const ensureReadyPromise = () => {
  if (!readyPromise) {
    readyPromise = new Promise((resolve) => {
      readyResolve = resolve;
    });
  }
  return readyPromise;
};

const resolveReady = () => {
  if (readyResolve) {
    readyResolve();
    readyResolve = null;
  }
};

const notifyListeners = () => {
  listeners.forEach((listener) => listener(authState));
};

const applySession = (session: Session | null) => {
  authState = {
    session,
    user: session?.user ?? null,
    loading: false,
  };
  notifyListeners();
  resolveReady();
};

export const initializeAuthState = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    authState = { session: null, user: null, loading: false };
    return;
  }

  if (initialized) {
    await ensureReadyPromise();
    return;
  }

  initialized = true;
  ensureReadyPromise();

  if (!authSubscription) {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      hasAuthEvent = true;
      applySession(session);
    });
    authSubscription = data;
  }

  try {
    const { data, error } = await supabase.auth.getSession();
    if (!hasAuthEvent) {
      applySession(data.session ?? null);
    }
    // `getSession` reports `{ session: null, error }` when the stored access
    // token has expired and the refresh did not go through
    // (`GoTrueClient.js:2554-2575`). The two cases behind that look identical
    // here and must not be treated alike:
    //
    //  - Transient (network) — auth-js deliberately LEAVES the session in
    //    storage (`:4267-4285`), so it is recoverable. Settling this as "signed
    //    out" and stopping is how opening the app on a flaky connection used to
    //    look like a logout.
    //  - Terminal (dead refresh token) — auth-js has already REMOVED the stored
    //    session. Handing this to the recovery ladder would have it find an
    //    empty slot, classify `NoSession`, and retire quietly: no route capture,
    //    no re-auth modal, no restore. The user would sit on a signed-out app
    //    with nothing telling them to sign in.
    //
    // Classification therefore happens here, with the same rules the refresh
    // path uses, before anything is dispatched.
    if (error && !data.session) {
      // The refresh `getSession` ran internally has already populated auth-js's
      // 60s failure cache — tell the shared attempt clock so the ladder does not
      // spend its first step reading it back.
      noteExternalRefreshAttempt();

      const classified = classifyAuthRefreshError(error);
      if (classified.outcome === AuthRefreshOutcome.Terminal) {
        reportTerminalRefreshOutcome(classified.reason);
      } else {
        beginAuthRecovery();
      }
    }
  } catch {
    if (!hasAuthEvent) {
      applySession(null);
    }
  }
};

export const waitForAuthReady = async (): Promise<void> => {
  if (typeof window === 'undefined') return;
  await initializeAuthState();
};

export const subscribeAuthState = (listener: AuthListener): (() => void) => {
  listeners.add(listener);
  listener(authState);
  return () => {
    listeners.delete(listener);
  };
};

export const getAuthState = (): AuthState => authState;

export const getUserId = (): string | null => authState.user?.id ?? null;

export const getAccessToken = (): string | null => authState.session?.access_token ?? null;

export const setAuthSession = (session: Session | null): void => {
  hasAuthEvent = true;
  applySession(session);
};
